import React, { useEffect, useMemo, useRef, useState } from "react";
import { http, streamSSE } from "../lib/api";
import MarkdownLite from "../components/MarkdownLite";
import { applyCandidateFilters, filtersActive, filterSummary } from "../lib/filters";
import {
  Sparkle, Funnel, IdentificationCard, FileText, LinkedinLogo,
  NotePencil, Microphone, ArrowRight, Plus,
} from "@phosphor-icons/react";

const TABS = [
  { key: "profile", label: "Profile Summary", icon: IdentificationCard },
  { key: "resume", label: "Resume Analysis", icon: FileText },
  { key: "linkedin", label: "LinkedIn Data", icon: LinkedinLogo },
  { key: "notes", label: "Screening Notes", icon: NotePencil },
  { key: "interview", label: "Interview Transcriptions", icon: Microphone },
];

const STATUS_PILL = {
  New: "pill-new", Screening: "pill-screening", Interview: "pill-interview",
  Offer: "pill-offer", Hired: "pill-hired", Rejected: "pill-rejected",
};

const STATUSES = ["New", "Screening", "Interview", "Offer", "Hired", "Rejected"];

export default function CandidateScreening({ preselected, statusFilter = "All", locationFilter = "All", requisitionFilter = "All" }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(preselected || null);
  const [tab, setTab] = useState("profile");

  const loadList = async () => {
    const r = await http.get("/candidates");
    setCandidates(r.data);
    if (preselected) {
      const fresh = r.data.find((c) => c.id === preselected.id);
      if (fresh) setSelected(fresh);
    } else if (!selected && r.data[0]) {
      setSelected(r.data[0]);
    }
  };
  useEffect(() => { loadList(); }, []);

  // Whenever the user opens the Screening page or switches back to the
  // Profile / Resume / LinkedIn tab, silently refresh the selected candidate
  // so cross-page edits (CV upload, LinkedIn PDF import) appear immediately.
  useEffect(() => {
    if (selected && (tab === "profile" || tab === "resume" || tab === "linkedin")) {
      refreshSelected().catch(() => {});
    }
  }, [tab, selected?.id]);

  const visibleCandidates = useMemo(
    () => applyCandidateFilters(candidates, statusFilter, locationFilter, requisitionFilter),
    [candidates, statusFilter, locationFilter, requisitionFilter],
  );

  const selectCandidate = async (c) => {
    // Always fetch the freshest copy on click so the drawer reflects any
    // edits made from other pages (e.g. a newly-uploaded CV via CV Evaluation).
    setSelected(c);
    setTab("profile");
    try {
      const r = await http.get(`/candidates/${c.id}`);
      setSelected(r.data);
      setCandidates((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
    } catch (_e) { /* fall back to cached copy */ }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await http.get(`/candidates/${selected.id}`);
    setSelected(r.data);
    setCandidates((prev) => prev.map((c) => (c.id === r.data.id ? r.data : c)));
  };

  return (
    <div className="space-y-5" data-testid="candidate-screening-page">
      <div>
        <div className="label-eyebrow">Pre-Hire</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Candidate Screening</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Deep candidate intelligence — profile, resume, LinkedIn, screening notes & interview transcripts in one view.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
        {/* Candidate list */}
        <div className="card p-3 max-h-[80vh] overflow-y-auto">
          <div className="px-2 py-2 flex items-center gap-2">
            <Funnel size={14} className="text-slate-400" />
            <span className="label-eyebrow">Candidates</span>
            <span className="ml-auto text-[10px] text-slate-400" data-testid="screen-candidate-count">
              {visibleCandidates.length}/{candidates.length}
            </span>
          </div>
          {filtersActive(statusFilter, locationFilter, requisitionFilter) && (
            <div className="mx-2 mb-2 text-[10px] text-[#002FA7] bg-indigo-50 rounded px-2 py-1" data-testid="screen-filter-active">
              Sidebar filter: {filterSummary(statusFilter, locationFilter, requisitionFilter)}
            </div>
          )}
          <div className="flex flex-col">
            {visibleCandidates.map((c) => (
              <button
                key={c.id}
                data-testid={`screen-candidate-${c.id}`}
                className={`text-left px-3 py-2 border-l-2 text-sm hover:bg-slate-50 transition ${
                  selected?.id === c.id ? "bg-indigo-50 border-l-[#002FA7] font-semibold" : "border-l-transparent"
                }`}
                onClick={() => { selectCandidate(c); }}
              >
                <div>{c.name}</div>
                <div className="text-[11px] text-slate-500">{c.position} · {c.matchScore}/100</div>
              </button>
            ))}
            {visibleCandidates.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid="screen-empty">
                No candidates match the sidebar filter.
              </div>
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="card overflow-hidden">
          {!selected ? (
            <div className="p-10 text-center text-slate-400 text-sm">Select a candidate.</div>
          ) : (
            <>
              <DetailHeader candidate={selected} onChanged={refreshSelected} />
              <div className="tabs-bar px-4">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      data-testid={`screen-tab-${t.key}`}
                      className={`tab-btn flex items-center gap-2 ${tab === t.key ? "active" : ""}`}
                      onClick={() => setTab(t.key)}
                    >
                      <Icon size={14} weight={tab === t.key ? "fill" : "regular"} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <div className="p-6">
                {tab === "profile" && <ProfileTab c={selected} />}
                {tab === "resume" && <ResumeTab c={selected} />}
                {tab === "linkedin" && <LinkedInTab c={selected} onChanged={refreshSelected} />}
                {tab === "notes" && <NotesTab c={selected} onChanged={refreshSelected} />}
                {tab === "interview" && <InterviewTab c={selected} onChanged={refreshSelected} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailHeader({ candidate, onChanged }) {
  const [status, setStatus] = useState(candidate.status);
  const [saving, setSaving] = useState(false);
  useEffect(() => setStatus(candidate.status), [candidate.id, candidate.status]);
  const save = async () => {
    setSaving(true);
    try {
      await http.patch(`/candidates/${candidate.id}/status`, { status });
      await onChanged();
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="px-6 pt-5 pb-3 border-b">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Candidate Screening</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1">{candidate.name}</h2>
          <div className="text-sm text-slate-500">
            {candidate.position} · {candidate.location} · Match {candidate.matchScore}/100
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="label-eyebrow !text-[10px] mb-1">Current Status</div>
            <span className={`pill ${STATUS_PILL[candidate.status]}`}>{candidate.status}</span>
          </div>
          <div>
            <div className="label-eyebrow !text-[10px] mb-1">Change Status</div>
            <select
              data-testid="screen-status-select"
              className="input !py-1.5"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <button
            data-testid="screen-update-status"
            className="btn btn-primary"
            onClick={save}
            disabled={saving || status === candidate.status}
          >
            {saving ? "Saving…" : "Update Status"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ c }) {
  const [assessment, setAssessment] = useState("");
  const [streaming, setStreaming] = useState(false);
  const run = async () => {
    setAssessment("");
    setStreaming(true);
    await streamSSE(`/candidates/${c.id}/ai/skills-assessment`, {}, {
      onDelta: (_d, full) => setAssessment(full),
      onDone: () => setStreaming(false),
      onError: () => setStreaming(false),
    });
  };
  return (
    <div className="space-y-5" data-testid="tab-profile">
      <Section title="Profile Summary">
        <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
          {c.profileSummary || (c.linkedin?.summary) || "No profile summary on file."}
        </p>
      </Section>

      {c.resumeText && (
        <Section
          title={`Uploaded CV / Resume ${c.resumeSource ? `· ${c.resumeSource}` : ""}`}
        >
          <div className="text-xs text-slate-500 mb-2">
            {c.resumeUploadedBy === "candidate"
              ? "Submitted by the candidate via the self-service portal."
              : "Uploaded by the recruiter."}
            {c.resumeUpdatedAt && ` · ${new Date(c.resumeUpdatedAt).toLocaleString()}`}
          </div>
          <pre className="text-xs whitespace-pre-wrap font-mono bg-[#fafbfd] border rounded-sm p-3 max-h-96 overflow-auto">
            {c.resumeText}
          </pre>
        </Section>
      )}

      <Section
        title="Skills Match Assessment"
        action={
          <button
            data-testid="run-skills-assessment"
            className="btn btn-primary !py-1 !px-3 !text-xs"
            onClick={run}
            disabled={streaming}
          >
            <Sparkle size={12} weight="fill" />
            {streaming ? "Generating…" : "Generate Assessment"}
          </button>
        }
      >
        <div className="border bg-[#fafbfd] rounded-sm p-4 min-h-[180px]">
          {assessment ? (
            <>
              <MarkdownLite text={assessment} />
              {streaming && <span className="cursor-blink" />}
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-slate-500">
              <div>
                <div className="font-display font-bold text-slate-700 mb-1">Technical Skills Assessment</div>
                Click <em>Generate Assessment</em> to get an AI-evaluated technical breakdown for {c.name}.
              </div>
              <div>
                <div className="font-display font-bold text-slate-700 mb-1">Leadership Assessment</div>
                A structured leadership signal & growth-potential analysis will appear here.
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

function ResumeTab({ c }) {
  const r = c.resume || {};
  // Fall back to LinkedIn-imported data (from PDF parse) if the structured
  // resume block is empty — so the LinkedIn PDF import shows up here too.
  const li = c.linkedin || {};
  const workExperience = (r.workExperience && r.workExperience.length > 0)
    ? r.workExperience
    : (Array.isArray(li.experience) ? li.experience : []);
  const education = (r.education && r.education.length > 0)
    ? r.education
    : (Array.isArray(li.education) ? li.education : []);
  const certifications = (r.certifications && r.certifications.length > 0)
    ? r.certifications
    : (Array.isArray(li.certifications) ? li.certifications : []);
  const skillCats = r.skillCategories || {};
  // If no categorized skills, fall back to the flat top-level skills list.
  const flatSkills = (Array.isArray(c.skills) ? c.skills : []).filter(Boolean);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tab-resume">
      {/* Work experience – takes 2 cols */}
      <div className="lg:col-span-2 space-y-4">
        <h3 className="font-display text-lg font-bold">Work Experience</h3>
        {workExperience.length === 0 && (
          <div className="text-sm text-slate-400">
            {c.resumeText
              ? "No structured experience parsed. See the Profile Summary tab for the raw uploaded CV text."
              : "No work experience uploaded. Ask the candidate to attach a CV via the portal or upload a LinkedIn PDF."}
          </div>
        )}
        {workExperience.map((w, i) => (
          <div key={i} className="border-l-2 border-[#002FA7] pl-4">
            <div className="font-semibold text-sm">{w.title} <span className="text-slate-500 font-normal">at {w.company}</span></div>
            <div className="text-[11px] font-mono text-slate-500">{w.period}</div>
            <p className="text-sm text-slate-700 mt-1">{w.summary}</p>
          </div>
        ))}
      </div>
      <div className="space-y-5">
        <div>
          <h3 className="font-display text-lg font-bold mb-2">Education</h3>
          {education.length === 0 ? (
            <div className="text-sm text-slate-400">—</div>
          ) : education.map((e, i) => (
            <div key={i} className="text-sm">
              <div className="font-semibold">{e.degree}</div>
              <div className="text-slate-500 text-xs">{e.school}{e.year ? ` · ${e.year}` : ""}</div>
            </div>
          ))}
        </div>
        <div>
          <h3 className="font-display text-lg font-bold mb-2">Skills &amp; Certifications</h3>
          {Object.keys(skillCats).length === 0 && flatSkills.length === 0 && certifications.length === 0 && (
            <div className="text-sm text-slate-400">—</div>
          )}
          {Object.entries(skillCats).map(([cat, items]) => (
            <div key={cat} className="mb-3">
              <div className="label-eyebrow !text-[10px] mb-1">{cat}</div>
              <ul className="text-sm text-slate-700 space-y-0.5">
                {items.map((s, i) => (
                  <li key={i}>• {s.name}{s.years ? ` (${s.years} yrs)` : ""}</li>
                ))}
              </ul>
            </div>
          ))}
          {/* Flat skill list — used when the candidate came from a LinkedIn PDF
              or manual entry and there are no category buckets. */}
          {Object.keys(skillCats).length === 0 && flatSkills.length > 0 && (
            <div className="mb-3">
              <div className="label-eyebrow !text-[10px] mb-1">Skills</div>
              <ul className="text-sm text-slate-700 space-y-0.5">
                {flatSkills.map((s, i) => <li key={i}>• {s}</li>)}
              </ul>
            </div>
          )}
          {certifications.length > 0 && (
            <div>
              <div className="label-eyebrow !text-[10px] mb-1">Certification</div>
              <ul className="text-sm text-slate-700">
                {certifications.map((c2, i) => <li key={i}>• {c2}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LinkedInTab({ c, onChanged }) {
  const l = c.linkedin || {};
  // The PDF importer stores headline/summary/experience on `linkedin.*` but
  // doesn't write `linkedin.url`; the top-level `candidate.linkedinUrl` is
  // the actual URL source of truth. Support both here.
  const url = l.url || c.linkedinUrl || "";
  const hasAnyData = url || l.headline || l.summary || (l.experience || []).length > 0;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await http.post(`/candidates/${c.id}/refresh-profile`, {});
      const s = r.data?.scrape || {};
      setMsg(
        s.name || s.title || s.description
          ? `Fetched: ${[s.name, s.title].filter(Boolean).join(" · ")}`
          : "LinkedIn blocked the public scrape. URL is saved — try again later or ask the candidate for their public profile URL.",
      );
      if (onChanged) await onChanged();
    } catch (e) {
      setMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="tab-linkedin">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <LinkedinLogo size={20} weight="fill" className="text-[#0a66c2]" /> LinkedIn Profile
        </h3>
        {url && (
          <button
            data-testid="linkedin-fetch-now"
            className="btn btn-primary !py-1.5 !px-3 !text-xs"
            onClick={fetchNow}
            disabled={busy}
          >
            {busy ? "Fetching…" : "Fetch from LinkedIn"}
          </button>
        )}
      </div>
      {!hasAnyData ? (
        <div className="text-sm text-slate-400">No LinkedIn data yet. Add a URL from the Candidate List drawer, or upload the candidate&apos;s LinkedIn PDF export.</div>
      ) : (
        <div className="card p-5 space-y-3 max-w-xl">
          <div><span className="label-eyebrow !text-[10px]">Headline</span><div className="text-sm">{l.headline || <span className="text-slate-400 italic">— not imported yet —</span>}</div></div>
          {url && (
            <div><span className="label-eyebrow !text-[10px]">Profile URL</span>
              <div className="text-sm font-mono break-all">
                <a href={url} target="_blank" rel="noreferrer noopener" className="text-[#0a66c2] hover:underline">{url}</a>
              </div>
            </div>
          )}
          {l.location && (
            <div><span className="label-eyebrow !text-[10px]">Location</span><div className="text-sm">{l.location}</div></div>
          )}
          {l.summary && (
            <div><span className="label-eyebrow !text-[10px]">Summary</span><div className="text-sm text-slate-600 whitespace-pre-wrap">{l.summary}</div></div>
          )}
          {(l.experience || []).length > 0 && (
            <div>
              <span className="label-eyebrow !text-[10px]">Experience ({l.experience.length})</span>
              <div className="text-sm space-y-1 mt-1">
                {l.experience.map((e, i) => (
                  <div key={i} className="border-l-2 border-slate-200 pl-2">
                    <div className="font-medium">{e.title}{e.company ? ` — ${e.company}` : ""}</div>
                    {e.period && <div className="text-[11px] font-mono text-slate-500">{e.period}</div>}
                    {e.summary && <div className="text-xs text-slate-600">{e.summary}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(l.education || []).length > 0 && (
            <div>
              <span className="label-eyebrow !text-[10px]">Education</span>
              <div className="text-sm space-y-1 mt-1">
                {l.education.map((e, i) => (
                  <div key={i}><span className="font-medium">{e.school}</span>{e.degree ? ` — ${e.degree}` : ""}</div>
                ))}
              </div>
            </div>
          )}
          {(l.certifications || []).length > 0 && (
            <div>
              <span className="label-eyebrow !text-[10px]">Certifications</span>
              <ul className="text-sm text-slate-600 mt-1">
                {l.certifications.map((cn, i) => <li key={i}>• {cn}</li>)}
              </ul>
            </div>
          )}
          {l.importedFrom && (
            <div className="text-[10px] text-slate-400 pt-2 border-t">
              Imported from <strong>{l.importedFrom}</strong>
              {l.importedBy && ` (${l.importedBy})`}
              {l.importedAt && ` · ${new Date(l.importedAt).toLocaleString()}`}
            </div>
          )}
          {c.linkedinRefreshedAt && !l.importedAt && (
            <div className="text-[10px] text-slate-400">Last refreshed: {new Date(c.linkedinRefreshedAt).toLocaleString()}</div>
          )}
        </div>
      )}
      {msg && (
        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-2 max-w-xl" data-testid="linkedin-fetch-msg">
          {msg}
        </div>
      )}
    </div>
  );
}

function NotesTab({ c, onChanged }) {
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("HR Manager");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await http.post(`/candidates/${c.id}/notes`, { author, note: text });
      setText("");
      await onChanged();
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="tab-notes">
      <h3 className="font-display text-lg font-bold">Screening Notes</h3>
      {(c.screeningNotes || []).length === 0 && (
        <div className="text-sm text-slate-400">No screening notes yet.</div>
      )}
      {(c.screeningNotes || []).map((n, i) => (
        <div key={i} className="border-l-2 border-[#002FA7] pl-4">
          <div className="text-xs text-slate-500 font-mono">{n.ts} <span className="italic text-slate-400">· {n.author}</span></div>
          <p className="text-sm text-slate-700 mt-0.5">{n.note}</p>
        </div>
      ))}
      <div className="pt-4 border-t">
        <h4 className="font-display font-bold text-sm mb-2">Add Screening Note</h4>
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2">
          <input
            data-testid="note-author"
            className="input"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Author"
          />
          <textarea
            data-testid="note-text"
            className="input"
            placeholder="Enter screening note…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <button data-testid="note-add" className="btn btn-primary mt-2" onClick={submit} disabled={saving || !text.trim()}>
          <Plus size={14} weight="bold" /> {saving ? "Adding…" : "Add Note"}
        </button>
      </div>
    </div>
  );
}

function InterviewTab({ c, onChanged }) {
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("Interviewer");
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState("");
  const [streaming, setStreaming] = useState(false);

  const addTranscript = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await http.post(`/candidates/${c.id}/transcripts`, { author, text });
      setText("");
      await onChanged();
    } finally { setSaving(false); }
  };
  const generate = async () => {
    // If the user typed a fresh transcript but hasn't clicked "Add Text",
    // save it first so the LLM has something to summarize (avoids HTTP 400).
    if (text.trim()) {
      setSaving(true);
      try {
        await http.post(`/candidates/${c.id}/transcripts`, { author, text });
        setText("");
        await onChanged();
      } catch (_e) { /* fall through so the LLM call still surfaces its own error */ }
      finally { setSaving(false); }
    }
    // If we still have nothing (no prior transcripts + nothing typed), tell
    // the user directly instead of letting the backend return HTTP 400.
    if (!text.trim() && (c.interviewTranscripts || []).length === 0 && (c.screeningNotes || []).length === 0) {
      setSummary("Add interview text (or a screening note) first — there's nothing to summarize yet.");
      return;
    }
    setSummary("");
    setStreaming(true);
    await streamSSE(`/candidates/${c.id}/ai/interview-summary`, {}, {
      onDelta: (_d, full) => setSummary(full),
      onDone: () => setStreaming(false),
      onError: (e) => { setStreaming(false); setSummary(`Error: ${e.message}`); },
    });
  };
  return (
    <div className="space-y-4" data-testid="tab-interview">
      <h3 className="font-display text-lg font-bold">Interview Notes &amp; Transcriptions</h3>
      {(c.interviewTranscripts || []).length === 0 && (
        <div className="text-sm text-slate-400">No interview transcripts yet.</div>
      )}
      {(c.interviewTranscripts || []).map((n, i) => (
        <div key={i} className="border-l-2 border-emerald-500 pl-4">
          <div className="text-xs text-slate-500 font-mono">{n.ts} <span className="italic text-slate-400">· {n.author}</span></div>
          <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{n.text}</p>
        </div>
      ))}
      <div className="pt-4 border-t">
        <h4 className="font-display font-bold text-sm mb-2">Add Interview Transcript</h4>
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2">
          <input
            data-testid="transcript-author"
            className="input"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Author"
          />
          <textarea
            data-testid="transcript-text"
            className="input"
            style={{ minHeight: 120 }}
            placeholder="Paste or enter interview transcript…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          <button data-testid="transcript-add" className="btn btn-outline" onClick={addTranscript} disabled={saving || !text.trim()}>
            <Plus size={14} weight="bold" /> {saving ? "Adding…" : "Add Text"}
          </button>
          <button data-testid="generate-interview-summary" className="btn btn-primary" onClick={generate} disabled={streaming}>
            <Sparkle size={14} weight="fill" />
            {streaming ? "Generating…" : "Generate Interview Summary"}
            <ArrowRight size={12} />
          </button>
        </div>
        {(summary || streaming) && (
          <div className="mt-3 border bg-[#fafbfd] rounded-sm p-4">
            <MarkdownLite text={summary} />
            {streaming && <span className="cursor-blink" />}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-lg font-bold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
